import { ReactElement, memo } from "react";
import { Dimensions, Platform } from "react-native";
import DraggableFlatList, {
  DragEndParams,
  RenderItem,
} from "react-native-draggable-flatlist";

import ItemSeparator from "@/components/molecules/ItemSeparator";
import ListEmpty from "@/components/molecules/ListEmpty";
import SkeletonList from "@/components/organisms/SkeletonList";

import { FlatList } from "@/components/atoms/FlatList";
import { RefreshControl } from "@/components/atoms/RefreshControl";
import { PlayerCardData } from "@/types/playerCardTypes";
import { ifWeb } from "@/utils/platform";

import type { ListRenderItem } from "@/components/atoms/FlashList";

/**
 * Key extractor for player items
 * @param item - The player card data
 * @returns A stable, position-independent key so rows keep their identity
 *   (and drag state) across reorders instead of remounting.
 */
const keyExtractor = (item: PlayerCardData) => item.id;

/**
 * Props for the DraggableListView component
 */
interface DraggableListProps {
  loading: boolean;
  refreshing: boolean;
  players: PlayerCardData[];
  heightRecommendation?: number;
  isSmallScreen: boolean;
  onLoadMore?: () => void;
  onRefresh?: () => void;
  onDragEnd?: (params: DragEndParams<PlayerCardData>) => void;
  renderItem: RenderItem<PlayerCardData>;
  webRenderItem: ListRenderItem<PlayerCardData>;
  renderListFooterComponent: () => ReactElement | null;
}

/**
 * Renders a platform-specific draggable player list with loading and empty states
 * @param root0 - The component props
 * @param root0.heightRecommendation - Optional recommended height for list items in pixels
 * @param root0.players - Array of watchlist player fragments to display
 * @param root0.refreshing - Whether the list is being refreshed via pull-to-refresh
 * @param root0.isSmallScreen - Whether screen width is below mobile breakpoint
 * @param root0.onDragEnd - Callback invoked when user completes a drag-and-drop reorder
 * @param root0.onRefresh - Callback invoked when user pulls to refresh the list
 * @param root0.onLoadMore - Callback invoked when user scrolls to load more players
 * @param root0.renderItem - Function to render each player item in the list (native)
 * @param root0.webRenderItem - Function to render each player item in the list (web)
 * @param root0.loading - Whether player data is currently being fetched
 * @param root0.renderListFooterComponent - Function to render footer content below the list
 */
/**
 * Native draggable-list height style (fills the screen minus the header on
 * tablet/desktop-sized native viewports). The web list fills its flex parent
 * via `flex-1` instead — see SE-5373: a fixed measured height overflowed
 * shorter browser windows and broke scrolling.
 * design-system: dynamic-style waiver — data-driven value (measured screen height)
 * @param isSmallScreen - Whether the screen is mobile sized
 */
const listHeightStyle = (isSmallScreen: boolean) => ({
  height: isSmallScreen ? undefined : Dimensions.get("screen").height - 200,
});

/**
 * Draggable / flat player list with platform-specific rendering.
 * @param root0 - Component props
 * @param root0.heightRecommendation - Bottom padding for the recommendation banner
 * @param root0.players - Player list data
 * @param root0.refreshing - Whether a refresh is in progress
 * @param root0.isSmallScreen - Whether the screen is mobile sized
 * @param root0.onDragEnd - Drag-reorder handler
 * @param root0.onRefresh - Pull-to-refresh handler
 * @param root0.onLoadMore - Pagination handler
 * @param root0.renderItem - Native row renderer
 * @param root0.webRenderItem - Web row renderer
 * @param root0.loading - Whether data is loading
 * @param root0.renderListFooterComponent - Footer renderer
 */
const DraggableListView = ({
  heightRecommendation,
  players,
  refreshing,
  isSmallScreen,
  onDragEnd,
  onRefresh,
  onLoadMore,
  renderItem,
  webRenderItem,
  loading,
  renderListFooterComponent,
}: DraggableListProps) =>
  Platform.OS === "web" ? (
    <FlatList
      className="flex-1"
      contentContainerClassName={`flex-grow ${!isSmallScreen ? "px-2" : ""} pb-3 pt-0`}
      showsVerticalScrollIndicator={false}
      contentContainerStyle={{ paddingBottom: heightRecommendation }}
      data={players}
      refreshing={refreshing}
      keyExtractor={keyExtractor}
      onEndReachedThreshold={0.5}
      onRefresh={onRefresh}
      onEndReached={onLoadMore}
      renderItem={webRenderItem}
      refreshControl={
        <RefreshControl
          refreshing={refreshing}
          onRefresh={onRefresh}
          colors={["white"]}
          tintColor="white"
        />
      }
      ListEmptyComponent={
        loading || refreshing ? <SkeletonList type="player" /> : <ListEmpty />
      }
      ItemSeparatorComponent={ItemSeparator}
      ListFooterComponent={renderListFooterComponent}
    />
  ) : (
    <DraggableFlatList
      // eslint-disable-next-line no-restricted-syntax -- third-party draggable-flatlist (not the FlatList atom, not cross-convertible); runtime measured screen height (screen.height-200), no closed sizing token
      style={listHeightStyle(isSmallScreen)}
      contentContainerClassName={`flex-grow ${!isSmallScreen ? "px-2" : ""} pb-3 pt-0`}
      showsVerticalScrollIndicator={false}
      contentContainerStyle={{ paddingBottom: heightRecommendation }}
      data={players}
      refreshing={refreshing}
      keyExtractor={keyExtractor}
      removeClippedSubviews
      decelerationRate="normal"
      onEndReachedThreshold={0.5}
      onDragEnd={onDragEnd}
      onRefresh={onRefresh}
      onEndReached={onLoadMore}
      renderItem={renderItem}
      activationDistance={ifWeb(1, undefined)}
      refreshControl={
        <RefreshControl
          refreshing={refreshing}
          onRefresh={onRefresh}
          colors={["white"]}
          tintColor="white"
        />
      }
      ListEmptyComponent={
        loading || refreshing ? <SkeletonList type="player" /> : <ListEmpty />
      }
      ItemSeparatorComponent={ItemSeparator}
      ListFooterComponent={renderListFooterComponent}
    />
  );

DraggableListView.displayName = "DraggableListView";

export default memo(DraggableListView);
